/**
 * Type definitions for Aquarimate maintenance activities.
 * These types match the backend API responses for maintenance data.
 */

import { MAINTENANCE_CATEGORY_COLORS, CHART_FALLBACK_COLOR } from '@/utils/chartColors';

export interface MaintenanceActivity {
  activity_id: number;
  task_id: number;
  activity_date: string; // ISO 8601 datetime
  performed: boolean;
  task_title: string;
  task_type: number;
  tank_id?: number;
  tank_name?: string;
  equipment_name?: string;
  equipment_model?: string;
  notes?: string;
}

export interface MaintenanceActivityResponse {
  data: MaintenanceActivity[];
  count: number;
  start_date: string;
  end_date: string;
  task_type?: number;
}

/**
 * Aquarimate task type mappings.
 * These values come directly from the Aquarimate API.
 */
export const TASK_TYPE_LABELS: Record<number, string> = {
  0: 'Maintenance',    // Clean skimmer, calibrate probes, etc.
  1: 'Water Change',   // Water change tasks
  2: 'Dosing',         // Magnesium, nitrate, etc.
  3: 'Feeding',        // Feeding tasks
  4: 'Miscellaneous',  // ATO levels, filter roller, etc.
};

export const TASK_TYPE_COLORS: Record<number, string> = {
  0: MAINTENANCE_CATEGORY_COLORS.cleaning!,       // Orange - Maintenance
  1: MAINTENANCE_CATEGORY_COLORS.water_change!,    // Cyan - Water Change
  2: MAINTENANCE_CATEGORY_COLORS.dosing!,          // Pink - Dosing
  3: MAINTENANCE_CATEGORY_COLORS.feeding!,         // Purple - Feeding
  4: MAINTENANCE_CATEGORY_COLORS.miscellaneous!,   // Gray - Miscellaneous
};

/**
 * Get human-readable label for task type.
 * @param taskType - Numeric task type from API
 * @returns Label string for the task type
 */
export function getTaskTypeLabel(taskType: number): string {
  return TASK_TYPE_LABELS[taskType] || 'Other';
}

/**
 * Get color code for task type (for use in charts).
 * @param taskType - Numeric task type from API
 * @returns Hex color code
 */
export function getTaskTypeColor(taskType: number): string {
  return TASK_TYPE_COLORS[taskType] || CHART_FALLBACK_COLOR; // Fallback - Other/Unknown
}
